import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '@/src/shadComponents/ui/card';
import { Button } from '@/src/shadComponents/ui/button';
import CurrencyInput from '@/src/Common/components/CurrencyInput';
import useActivationPaymentSettings, {
    PaymentReportData,
    PaymentRecord,
} from './hooks/useActivationPaymentSettings';
import Loading from '@/src/Common/components/Loading';
import PaymentReportSummary from './PaymentReportSummary';
import PaymentReportChart from './PaymentReportChart';
import PaymentRecordsTable from './PaymentRecordsTable';
import { DatePickerWithRange } from '@/src/shadComponents/ui/DateRangePicker';
import { subDays, format } from 'date-fns';
import { Input } from '@/src/shadComponents/ui/input';

export default function PaymentSettings() {
    console.log('PaymentSettings component rendering...');

    const { activationId: id } = useParams();
    const activationId = id ? +id : 0;

    // Debug logging
    console.log(
        'PaymentSettings component mounted, activationId:',
        activationId,
    );

    const {
        getActivationPaymentSettings,
        createActivationPaymentSettings,
        updateActivationPaymentSettings,
        getPaymentReportData,
        getPaymentRecords,
        isLoading,
    } = useActivationPaymentSettings();

    const [paymentSettings, setPaymentSettings] = useState<any>(null);
    const [costInCents, setCostInCents] = useState<number>(0);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [permissionError, setPermissionError] = useState<boolean>(false);
    const [reportData, setReportData] = useState<PaymentReportData | null>(
        null,
    );
    const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
    const [paymentRecords, setPaymentRecords] = useState<PaymentRecord[]>([]);
    const [isLoadingRecords, setIsLoadingRecords] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<
        { from: Date; to: Date } | undefined
    >({
        from: subDays(new Date(), 30),
        to: new Date(),
    });

    // Stripe key form fields
    const [stripePublishableKey, setStripePublishableKey] = useState('');
    const [stripeSecretKey, setStripeSecretKey] = useState('');
    const [stripeEnvironment, setStripeEnvironment] = useState<'test' | 'live'>(
        'live',
    );
    const [showSecretKey, setShowSecretKey] = useState(false);

    // Load existing payment settings on component mount
    useEffect(() => {
        const loadPaymentSettings = async () => {
            try {
                const settings = await getActivationPaymentSettings(
                    activationId,
                );
                setPaymentSettings(settings);
                if (settings) {
                    setCostInCents(settings.costInCents);
                    setStripePublishableKey(
                        settings.stripePublishableKey || '',
                    );
                    setStripeEnvironment(
                        (settings.stripeEnvironment as 'test' | 'live') ||
                            'live',
                    );
                    // Never pre-populate secret key for security
                    setStripeSecretKey('');
                }
            } catch (err: any) {
                console.error('Error loading payment settings:', err);
                if (err.response?.status === 401) {
                    setPermissionError(true);
                }
            }
        };

        if (activationId) {
            loadPaymentSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    // Load payment report data and records when payment settings exist or date range changes
    useEffect(() => {
        const loadReportData = async () => {
            if (
                !paymentSettings ||
                !activationId ||
                !dateRange?.from ||
                !dateRange?.to
            )
                return;

            setIsLoadingReport(true);
            setIsLoadingRecords(true);

            try {
                const startDate = format(dateRange.from, 'yyyy-MM-dd');
                const endDate = format(dateRange.to, 'yyyy-MM-dd');

                // Load both report data and individual records in parallel
                const [reportData, records] = await Promise.all([
                    getPaymentReportData(activationId, startDate, endDate),
                    getPaymentRecords(activationId, startDate, endDate),
                ]);

                setReportData(reportData);
                setPaymentRecords(records);
            } catch (err: any) {
                console.error('Error loading payment data:', err);
                // Don't show error for report data - it's not critical
            } finally {
                setIsLoadingReport(false);
                setIsLoadingRecords(false);
            }
        };

        loadReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentSettings, activationId, dateRange]);

    // Track changes to enable/disable save button
    useEffect(() => {
        const originalCost = paymentSettings?.costInCents || 0;
        const originalPublishableKey =
            paymentSettings?.stripePublishableKey || '';
        const originalEnvironment =
            paymentSettings?.stripeEnvironment || 'live';

        const hasCostChanges = costInCents !== originalCost;
        const hasKeyChanges = stripePublishableKey !== originalPublishableKey;
        const hasEnvChanges = stripeEnvironment !== originalEnvironment;
        const hasSecretKey = stripeSecretKey.length > 0;

        setHasChanges(
            hasCostChanges || hasKeyChanges || hasEnvChanges || hasSecretKey,
        );
    }, [
        costInCents,
        stripePublishableKey,
        stripeSecretKey,
        stripeEnvironment,
        paymentSettings,
    ]);

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            let result;

            if (paymentSettings && paymentSettings?.costInCents) {
                // Update existing settings
                result = await updateActivationPaymentSettings({
                    activationId,
                    costInCents,
                    stripePublishableKey,
                    stripeSecretKey,
                    stripeEnvironment,
                });
            } else {
                // Create new settings
                result = await createActivationPaymentSettings({
                    activationId,
                    costInCents,
                    stripePublishableKey,
                    stripeSecretKey,
                    stripeEnvironment,
                });
            }

            if (result) {
                setPaymentSettings(result);
                setSaveSuccess(true);
                setHasChanges(false);

                // Clear secret key after save for security
                setStripeSecretKey('');

                // Reload report data and records after successful save
                try {
                    if (dateRange?.from && dateRange?.to) {
                        const startDate = format(dateRange.from, 'yyyy-MM-dd');
                        const endDate = format(dateRange.to, 'yyyy-MM-dd');

                        const [reportData, records] = await Promise.all([
                            getPaymentReportData(
                                activationId,
                                startDate,
                                endDate,
                            ),
                            getPaymentRecords(activationId, startDate, endDate),
                        ]);

                        setReportData(reportData);
                        setPaymentRecords(records);
                    }
                } catch (err) {
                    console.error('Error reloading payment data:', err);
                }

                // Clear success message after 3 seconds
                setTimeout(() => setSaveSuccess(false), 3000);
            } else {
                setSaveError('Failed to save payment settings');
            }
        } catch (err) {
            setSaveError('An unexpected error occurred');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        const originalCost = paymentSettings?.costInCents || 0;
        setCostInCents(originalCost);
        setStripePublishableKey(paymentSettings?.stripePublishableKey || '');
        setStripeEnvironment(
            (paymentSettings?.stripeEnvironment as 'test' | 'live') || 'live',
        );
        setStripeSecretKey('');
        setShowSecretKey(false);
        setSaveError(null);
        setSaveSuccess(false);
    };

    // Stripe key validation
    const validateStripeKey = (key: string, type: 'publishable' | 'secret') => {
        if (!key) return true; // Empty is valid (optional)

        if (type === 'publishable') {
            return key.startsWith('pk_test_') || key.startsWith('pk_live_');
        } else {
            return key.startsWith('sk_test_') || key.startsWith('sk_live_');
        }
    };

    const isPublishableKeyValid = validateStripeKey(
        stripePublishableKey,
        'publishable',
    );
    const isSecretKeyValid = validateStripeKey(stripeSecretKey, 'secret');

    if (isLoading) {
        return <Loading isLoading={true} />;
    }

    if (permissionError) {
        return (
            <div className="space-y-6">
                <CardHeader>
                    <CardTitle>Access Denied</CardTitle>
                    <CardDescription>
                        You need administrator privileges to manage payment
                        settings.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md bg-yellow-50 p-4">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm font-medium text-yellow-800">
                                    Only administrators can configure payment
                                    settings for activations.
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <CardHeader>
                <CardTitle>Payment Settings</CardTitle>
                <CardDescription>
                    Configure the cost per photo for this activation. Photos
                    will be charged at this rate when users make purchases.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div>
                    <label
                        htmlFor="cost-per-photo"
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                        Cost per Photo
                    </label>
                    <CurrencyInput
                        value={costInCents}
                        onChange={setCostInCents}
                        placeholder="$0.00"
                        disabled={isSaving}
                        error={saveError || undefined}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                        Set the price customers will pay for each photo
                    </p>
                </div>

                {/* Stripe API Configuration */}
                <div className="border-t pt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                        <span>Stripe API Configuration</span>
                        {paymentSettings?.hasStripeKeys && (
                            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                                Configured
                            </span>
                        )}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Configure activation-specific Stripe API keys. Leave
                        empty to use system defaults.
                    </p>

                    {/* Environment Selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Environment
                        </label>
                        <div className="flex space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    value="test"
                                    checked={stripeEnvironment === 'test'}
                                    onChange={(e) =>
                                        setStripeEnvironment(
                                            e.target.value as 'test',
                                        )
                                    }
                                    className="mr-2"
                                    disabled={isSaving}
                                />
                                Test Mode
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    value="live"
                                    checked={stripeEnvironment === 'live'}
                                    onChange={(e) =>
                                        setStripeEnvironment(
                                            e.target.value as 'live',
                                        )
                                    }
                                    className="mr-2"
                                    disabled={isSaving}
                                />
                                Live Mode
                            </label>
                        </div>
                    </div>

                    {/* Publishable Key */}
                    <div className="mb-4">
                        <label
                            htmlFor="stripe-publishable-key"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Stripe Publishable Key
                        </label>
                        <Input
                            id="stripe-publishable-key"
                            type="text"
                            value={stripePublishableKey}
                            onChange={(e) =>
                                setStripePublishableKey(e.target.value)
                            }
                            placeholder={`pk_${stripeEnvironment}_...`}
                            disabled={isSaving}
                            className={
                                !isPublishableKeyValid ? 'border-red-500' : ''
                            }
                        />
                        {!isPublishableKeyValid && stripePublishableKey && (
                            <p className="mt-1 text-sm text-red-600">
                                Publishable key must start with pk_test_ or
                                pk_live_
                            </p>
                        )}
                        <p className="mt-1 text-sm text-gray-500">
                            Used for client-side payment processing
                        </p>
                    </div>

                    {/* Secret Key */}
                    <div className="mb-4">
                        <label
                            htmlFor="stripe-secret-key"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Stripe Secret Key
                        </label>
                        <div className="relative">
                            <Input
                                id="stripe-secret-key"
                                type={showSecretKey ? 'text' : 'password'}
                                value={stripeSecretKey}
                                onChange={(e) =>
                                    setStripeSecretKey(e.target.value)
                                }
                                placeholder={
                                    paymentSettings?.hasStripeKeys
                                        ? 'Enter new secret key to update'
                                        : `sk_${stripeEnvironment}_...`
                                }
                                disabled={isSaving}
                                className={
                                    !isSecretKeyValid ? 'border-red-500' : ''
                                }
                            />
                            <button
                                type="button"
                                onClick={() => setShowSecretKey(!showSecretKey)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                disabled={isSaving}
                            >
                                {showSecretKey ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {!isSecretKeyValid && stripeSecretKey && (
                            <p className="mt-1 text-sm text-red-600">
                                Secret key must start with sk_test_ or sk_live_
                            </p>
                        )}
                        <p className="mt-1 text-sm text-gray-500">
                            Used for server-side payment processing. Stored
                            encrypted, and never sent outside of the server.
                        </p>
                    </div>

                    {/* Security Warning */}
                    {stripeSecretKey && (
                        <div className="rounded-md bg-yellow-50 p-4 mb-4">
                            <div className="flex">
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-yellow-800">
                                        ⚠️ Security Notice
                                    </p>
                                    <p className="text-sm text-yellow-700 mt-1">
                                        Secret keys are sensitive. They will be
                                        encrypted and stored securely. Never
                                        share these keys or commit them to
                                        version control.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {saveSuccess && (
                    <div className="rounded-md bg-green-50 p-4">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm font-medium text-green-800">
                                    Payment settings saved successfully!
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-between items-center">
                    <div>
                        {paymentSettings && paymentSettings.updatedAt ? (
                            <p className="text-sm text-gray-500">
                                Last updated:{' '}
                                {new Date(
                                    paymentSettings.updatedAt,
                                ).toLocaleDateString()}
                            </p>
                        ) : (
                            <p className="text-sm text-gray-500">
                                No payment settings configured yet
                            </p>
                        )}
                        {paymentSettings?.hasStripeKeys ? (
                            <p className="text-sm text-green-600 mt-1">
                                ✅ Custom Stripe keys configured
                            </p>
                        ) : (
                            <p className="text-sm text-gray-500 mt-1">
                                Using system default Stripe configuration
                            </p>
                        )}
                    </div>

                    <div className="flex space-x-3">
                        {hasChanges && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleReset}
                                disabled={isSaving}
                            >
                                Reset
                            </Button>
                        )}
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={
                                !hasChanges ||
                                !isPublishableKeyValid ||
                                !isSecretKeyValid ||
                                isSaving
                            }
                        >
                            {isSaving
                                ? 'Saving...'
                                : paymentSettings
                                ? 'Update Settings'
                                : 'Create Settings'}
                        </Button>
                    </div>
                </div>
            </CardContent>

            {/* Payment Reports Section - only show if payment settings exist */}
            {paymentSettings && (
                <div className="space-y-6 p-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Payment Reports
                        </h2>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setDateRange({
                                            from: subDays(new Date(), 7),
                                            to: new Date(),
                                        })
                                    }
                                >
                                    Last 7 days
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setDateRange({
                                            from: subDays(new Date(), 30),
                                            to: new Date(),
                                        })
                                    }
                                >
                                    Last 30 days
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setDateRange({
                                            from: subDays(new Date(), 90),
                                            to: new Date(),
                                        })
                                    }
                                >
                                    Last 90 days
                                </Button>
                            </div>
                            <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-700">
                                    Custom Range:
                                </label>
                                <DatePickerWithRange
                                    className="w-auto"
                                    date={dateRange}
                                    setDate={setDateRange}
                                />
                            </div>
                        </div>
                    </div>

                    {isLoadingReport || isLoadingRecords ? (
                        <div className="flex items-center justify-center py-8">
                            <Loading isLoading={true} />
                        </div>
                    ) : reportData ? (
                        <>
                            <PaymentReportSummary
                                reportData={reportData}
                                dateRange={dateRange}
                            />
                            <PaymentReportChart reportData={reportData} />
                            <PaymentRecordsTable
                                paymentRecords={paymentRecords}
                                isLoading={isLoadingRecords}
                            />
                        </>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            No payment data available for the selected date
                            range
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
